import { spawnSync } from "node:child_process";

import { readTextIfExists } from "../lib/fs.js";

export const DEEPSEEK_HARNESS_VERSION = "0.1.0-rc.6";
export const DEEPSEEK_ORGX_PLUGIN = "@useorgx/deepseek-harness-plugin";
export const DEEPSEEK_ORGX_PLUGIN_VERSION = "0.1.0";
export const DEEPSEEK_PROFILE = "headless";

export interface DeepseekCommandResult {
  status: number | null;
  stdout: string;
  stderr: string;
  error?: Error;
}

export type DeepseekCommandRunner = (
  command: string,
  args: readonly string[],
) => DeepseekCommandResult;

export interface DeepseekRuntimeInspection {
  installed: boolean;
  supported: boolean;
  version?: string;
  details: string[];
}

export interface DeepseekPluginInspection {
  installed: boolean;
  version?: string;
  details: string[];
}

export interface DeepseekPluginMutation {
  changed: boolean;
  message: string;
}

export const runDeepseekCommand: DeepseekCommandRunner = (command, args) => {
  const result = spawnSync(command, [...args], {
    encoding: "utf8",
    stdio: ["ignore", "pipe", "pipe"],
    timeout: 30_000,
  });

  return {
    status: result.status,
    stdout: result.stdout ?? "",
    stderr: result.stderr ?? "",
    ...(result.error ? { error: result.error } : {}),
  };
};

function normalizeVersionOutput(value: string): string | undefined {
  const normalized = value.trim();
  return /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/.test(normalized)
    ? normalized
    : undefined;
}

export function inspectDeepseekRuntime(
  runner: DeepseekCommandRunner = runDeepseekCommand,
): DeepseekRuntimeInspection {
  const result = runner("dsh", ["--version"]);
  const version = normalizeVersionOutput(result.stdout);

  if (result.status !== 0 || !version) {
    return {
      installed: false,
      supported: false,
      details: [
        result.error?.message ??
          (result.stderr.trim() || "dsh --version did not return a version"),
      ],
    };
  }

  const supported = version === DEEPSEEK_HARNESS_VERSION;
  return {
    installed: true,
    supported,
    version,
    details: [
      supported
        ? `DeepSeek Harness ${version} detected`
        : `DeepSeek Harness ${version} detected; OrgX currently requires ${DEEPSEEK_HARNESS_VERSION}`,
    ],
  };
}

function readDependencyVersion(raw: string | null): string | undefined {
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const key of ["dependencies", "devDependencies", "optionalDependencies"]) {
      const dependencies = parsed[key];
      if (!dependencies || typeof dependencies !== "object" || Array.isArray(dependencies)) {
        continue;
      }
      const version = (dependencies as Record<string, unknown>)[DEEPSEEK_ORGX_PLUGIN];
      if (typeof version === "string" && version.trim()) return version.trim();
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function inspectDeepseekPlugin(profileManifestPath: string): DeepseekPluginInspection {
  const version = readDependencyVersion(readTextIfExists(profileManifestPath));
  return {
    installed: Boolean(version),
    ...(version ? { version } : {}),
    details: [
      version
        ? `${DEEPSEEK_ORGX_PLUGIN} ${version} is installed in the ${DEEPSEEK_PROFILE} profile`
        : `${DEEPSEEK_ORGX_PLUGIN} is not installed in the ${DEEPSEEK_PROFILE} profile`,
    ],
  };
}

export function installDeepseekPlugin(
  profileManifestPath: string,
  runner: DeepseekCommandRunner = runDeepseekCommand,
): DeepseekPluginMutation {
  const runtime = inspectDeepseekRuntime(runner);
  if (!runtime.installed) {
    return {
      changed: false,
      message: `DeepSeek Harness was not found. Install @deepseek-ai/dsh@${DEEPSEEK_HARNESS_VERSION}, then run setup again.`,
    };
  }
  if (!runtime.supported) {
    return {
      changed: false,
      message: `DeepSeek Harness ${runtime.version ?? "unknown"} is not supported; OrgX requires ${DEEPSEEK_HARNESS_VERSION}.`,
    };
  }

  const before = inspectDeepseekPlugin(profileManifestPath);
  if (before.installed && before.version === DEEPSEEK_ORGX_PLUGIN_VERSION) {
    return {
      changed: false,
      message: `${DEEPSEEK_ORGX_PLUGIN}@${DEEPSEEK_ORGX_PLUGIN_VERSION} is already installed.`,
    };
  }

  const spec = `${DEEPSEEK_ORGX_PLUGIN}@${DEEPSEEK_ORGX_PLUGIN_VERSION}`;
  const result = runner("dsh", ["plugin", "--profile", DEEPSEEK_PROFILE, "add", spec]);
  if (result.status !== 0) {
    const detail = result.stderr.trim() || result.error?.message || `exit ${result.status ?? "unknown"}`;
    throw new Error(`Could not install ${spec}: ${detail}`);
  }

  return {
    changed: true,
    message: `${spec} installed in the ${DEEPSEEK_PROFILE} profile.`,
  };
}

export function removeDeepseekPlugin(
  profileManifestPath: string,
  runner: DeepseekCommandRunner = runDeepseekCommand,
): DeepseekPluginMutation {
  if (!inspectDeepseekPlugin(profileManifestPath).installed) {
    return {
      changed: false,
      message: `${DEEPSEEK_ORGX_PLUGIN} is not installed.`,
    };
  }

  const result = runner("dsh", [
    "plugin",
    "--profile",
    DEEPSEEK_PROFILE,
    "remove",
    DEEPSEEK_ORGX_PLUGIN,
  ]);
  if (result.status !== 0) {
    const detail = result.stderr.trim() || result.error?.message || `exit ${result.status ?? "unknown"}`;
    throw new Error(`Could not remove ${DEEPSEEK_ORGX_PLUGIN}: ${detail}`);
  }

  return {
    changed: true,
    message: `${DEEPSEEK_ORGX_PLUGIN} removed from the ${DEEPSEEK_PROFILE} profile.`,
  };
}
